/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { Contractor, ContractorResponse } from "@/models/api/administration/contractors/Contractor";
import {
  getContractorBankStatementItem,
  getContractorDocumentsWithPayment,
  getContractorsTable,
} from "@/requests/administration/contractors/contractorAdministrations";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  DocumentsWithPaymentsResponse,
  DocumentWithPayments,
} from "@/models/api/administration/contractors/DocumentsWithPaymentsResponse";
import {
  ContractorBankStatementItemResponse,
  ContractorStatementPaymentItem,
} from "@/models/api/administration/contractors/ContractorBankStatementItemResponse";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";

export interface ContractorSettlementInterface {
  isContractorSettlementDialog: boolean;
  selectedContractorSettlementTab: number;
  contractorDocumentSettlementFilter: null | Array<CriterionBase>;
  contractorPaymentSettlementFilter: null | Array<CriterionBase>;
  documentDataTableOptions: DataOptions;
  paymentDataTableOptions: DataOptions;
  contractorDocumentSettlement: Array<DocumentWithPayments>;
  contractorPaymentSettlement: Array<ContractorStatementPaymentItem>;
  countItems: number | null;
  leftToPaySum: null | string;
}

export const ContractorSettlementState: ContractorSettlementInterface = {
  isContractorSettlementDialog: false,
  selectedContractorSettlementTab: 0,
  contractorDocumentSettlementFilter: null,
  contractorPaymentSettlementFilter: null,
  documentDataTableOptions: new DefaultDataOptions(),
  paymentDataTableOptions: new DefaultDataOptions(),
  contractorDocumentSettlement: new Array<DocumentWithPayments>(),
  contractorPaymentSettlement: new Array<ContractorStatementPaymentItem>(),
  countItems: null,
  leftToPaySum: null,
};

export interface AdministrationContractorsStateInterface {
  contractorSettlement: ContractorSettlementInterface;
  contractorsFilters: null | Array<CriterionBase>;
  contractorFilterCriterions: Record<string, unknown>;
  contractors: Array<Contractor>;
  selectedContractorId: null | string;
  dataTableOptions: DataOptions;
  countItems: number | null;
  recordStampId: number | null;
}

export const AdministrationContractorsState: AdministrationContractorsStateInterface = {
  contractorSettlement: ContractorSettlementState,
  contractorsFilters: null,
  contractorFilterCriterions: {
    ContractorsAutocomplete: {
      uniqueId: null,
      value: "",
    },
  },
  contractors: [],
  selectedContractorId: null,
  dataTableOptions: new DefaultDataOptions(),
  countItems: null,
  recordStampId: null,
};

const AdministrationContractorsModule = defineModule({
  state: AdministrationContractorsState as AdministrationContractorsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setContractorFilterCriterion(state, param: CriterionItemRequest) {
      state.contractorFilterCriterions[param.key] = param.value;
    },
    setContractorsFilters(state, param: Array<CriterionBase>) {
      state.contractorsFilters = param;
    },
    setSelectedContractorId(state, param: null | string) {
      state.selectedContractorId = param;
    },
    setContractors(state, param: ContractorResponse) {
      state.contractors = param.items;
      state.countItems = param.totalCount;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setIsContractorSettlementDialog(state, param: boolean) {
      state.contractorSettlement.isContractorSettlementDialog = param;
    },
    setSelectedContractorSettlementTab(state, param: number) {
      state.contractorSettlement.selectedContractorSettlementTab = param;
    },
    setContractorSettlementDocumentFilter(state, param: Array<CriterionBase>) {
      state.contractorSettlement.contractorDocumentSettlementFilter = param;
    },
    setContractorSettlementPaymentFilter(state, param: Array<CriterionBase>) {
      state.contractorSettlement.contractorPaymentSettlementFilter = param;
    },
    setContractorDocumentSettlement(state, param: DocumentsWithPaymentsResponse) {
      state.contractorSettlement.contractorDocumentSettlement = param.items;
      state.contractorSettlement.countItems = param.totalCount;
      state.contractorSettlement.leftToPaySum = param.leftToPay;
    },
    setContractorsSettlementDocumentDataTableOptions(state, param: DataOptions) {
      state.contractorSettlement.documentDataTableOptions = param;
    },
    setContractorsSettlementPaymentDataTableOptions(state, param: DataOptions) {
      state.contractorSettlement.paymentDataTableOptions = param;
    },
    setContractorPaymentSettlement(state, param: ContractorBankStatementItemResponse) {
      state.contractorSettlement.contractorPaymentSettlement = param.items;
      state.contractorSettlement.countItems = param.totalCount;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
  },
  actions: {
    async downloadContractors(context, skipSetDefault: boolean | undefined) {
      const { commit, state } = _moduleActionContext(context);
      const contractors = await getContractorsTable({
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: _.cloneDeep(state.contractorsFilters),
        filterName: FILTER_NAMES.ContractorChooserStandardFilter,
      });
      commit.setContractors(contractors);
      if (!skipSetDefault && contractors.items.length > 0) {
        commit.setSelectedContractorId(contractors.items[0].id);
      }
    },
    async openContractorSettlementDialog(context, contractorId: string) {
      const { commit, dispatch } = _moduleActionContext(context);
      commit.setSelectedContractorId(contractorId);
      commit.setIsContractorSettlementDialog(true);
      dispatch.getContractorsDocumentSettlement();
    },
    async getContractorsDocumentSettlement(context) {
      const { commit, state } = _moduleActionContext(context);
      const documentsWithPayments = await getContractorDocumentsWithPayment(
        {
          count: state.contractorSettlement.documentDataTableOptions.itemsPerPage,
          page: state.contractorSettlement.documentDataTableOptions.page - 1,
          criterions: _.cloneDeep(state.contractorSettlement.contractorDocumentSettlementFilter),
          filterName: FILTER_NAMES.ContractorSettlementDocumentStandardFilter,
        },
        state.selectedContractorId as string
      );
      commit.setContractorDocumentSettlement(documentsWithPayments);
    },
    async getContractorPaymentSettlement(context) {
      const { commit, state } = _moduleActionContext(context);
      const contractorBankStatements = await getContractorBankStatementItem(
        {
          count: state.contractorSettlement.paymentDataTableOptions.itemsPerPage,
          page: state.contractorSettlement.paymentDataTableOptions.page - 1,
          criterions: _.cloneDeep(state.contractorSettlement.contractorPaymentSettlementFilter),
          filterName: FILTER_NAMES.ContractorSettlementPaymentsStandardFilter,
        },
        state.selectedContractorId as string
      );
      commit.setContractorPaymentSettlement(contractorBankStatements);
    },
  },
});

export default AdministrationContractorsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorsModule);
